import React from 'react';
import {
  Grid,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  fetchAdminDashboardStats,
  fetchActivitiesToValidate,
  fetchPaymentsToPay,
  fetchPendingCandidatures,
} from '../api/dashboard';
import { listUsers } from '../api/users';
import { listPostsSummary } from '../api/posts';
import { listCandidatures } from '../api/candidatures';
import { listActivities } from '../api/activities';
import { listPayments } from '../api/payments';
import { useScopedSchoolIds } from '../auth/useScope';
import { paymentStatusLabel } from '../utils/statusLabels';

const formatDate = (date?: Date) =>
  date ? date.toLocaleDateString() : '—';

const formatAmount = (amount?: number) =>
  amount != null ? `${amount.toFixed(0)} CFA` : '—';

export const DashboardPage: React.FC = () => {
  const { isScopedRole, scopedSchoolIds, schools } = useScopedSchoolIds();

  // --- Données globales (admin / drh) ---
  const { data: stats } = useQuery({
    queryKey: ['admin-dashboard-stats'],
    queryFn: fetchAdminDashboardStats,
    enabled: !isScopedRole,
  });

  const { data: pendingCandidatures } = useQuery({
    queryKey: ['pending-candidatures'],
    queryFn: () => fetchPendingCandidatures(5),
    enabled: !isScopedRole,
  });

  const { data: activitiesToValidate } = useQuery({
    queryKey: ['activities-to-validate'],
    queryFn: () => fetchActivitiesToValidate(5),
    enabled: !isScopedRole,
  });

  const { data: paymentsToPay } = useQuery({
    queryKey: ['payments-to-pay'],
    queryFn: () => fetchPaymentsToPay(5),
    enabled: !isScopedRole,
  });

  // --- Données utilisées par les deux modes ---
  const { data: users } = useQuery({
    queryKey: ['users-for-dashboard'],
    queryFn: listUsers,
  });

  const { data: posts } = useQuery({
    queryKey: ['posts-summary-for-dashboard'],
    queryFn: listPostsSummary,
  });

  // --- Données scopées (cap / ae), calculées côté client ---
  const { data: scopedCandidatures } = useQuery({
    queryKey: ['candidatures'],
    queryFn: listCandidatures,
    enabled: isScopedRole,
  });
  const { data: scopedActivities } = useQuery({
    queryKey: ['activities-all-for-dashboard'],
    queryFn: () => listActivities(),
    enabled: isScopedRole,
  });
  const { data: scopedPayments } = useQuery({
    queryKey: ['payments-all-for-dashboard'],
    queryFn: () => listPayments(),
    enabled: isScopedRole,
  });

  const userNameById = React.useMemo(() => {
    const map: Record<string, string> = {};
    users?.forEach((u) => {
      const fullName = `${u.lastName ?? ''} ${u.firstName ?? ''}`.trim();
      map[u.id] = fullName || u.email || u.id;
    });
    return map;
  }, [users]);

  const postTitleById = React.useMemo(() => {
    const map: Record<string, string> = {};
    posts?.forEach((p) => {
      map[p.id] = p.title || p.id;
    });
    return map;
  }, [posts]);

  // Chaînes de rattachement à une école : poste → école, candidature → école, activité → école.
  const postSchoolById = React.useMemo(() => {
    const map: Record<string, string> = {};
    posts?.forEach((p) => {
      if (p.schoolId) map[p.id] = p.schoolId;
    });
    return map;
  }, [posts]);

  const candidatureSchoolById = React.useMemo(() => {
    const map: Record<string, string> = {};
    scopedCandidatures?.forEach((c) => {
      const schoolId = c.postId ? postSchoolById[c.postId] : undefined;
      if (schoolId) map[c.id] = schoolId;
    });
    return map;
  }, [scopedCandidatures, postSchoolById]);

  const activitySchoolById = React.useMemo(() => {
    const map: Record<string, string> = {};
    scopedActivities?.forEach((a) => {
      const schoolId = a.candidatureId
        ? candidatureSchoolById[a.candidatureId]
        : undefined;
      if (schoolId) map[a.id] = schoolId;
    });
    return map;
  }, [scopedActivities, candidatureSchoolById]);

  const inScope = React.useCallback(
    (schoolId?: string) => !!schoolId && !!scopedSchoolIds?.has(schoolId),
    [scopedSchoolIds]
  );

  const scoped = React.useMemo(() => {
    if (!isScopedRole) return null;

    const myCandidatures = (scopedCandidatures ?? []).filter((c) =>
      inScope(candidatureSchoolById[c.id])
    );
    const myPosts = (posts ?? []).filter((p) => inScope(p.schoolId));
    const myActivities = (scopedActivities ?? []).filter((a) =>
      inScope(activitySchoolById[a.id])
    );
    const myPayments = (scopedPayments ?? []).filter((p) =>
      inScope(p.activityId ? activitySchoolById[p.activityId] : undefined)
    );
    const mySchools = (schools ?? []).filter(
      (s) => scopedSchoolIds?.has(s.id) && (s.status ?? 'active') !== 'inactive'
    );

    const byDateDesc = <T extends { date?: Date }>(arr: T[]) =>
      [...arr].sort(
        (a, b) => (b.date?.getTime() ?? 0) - (a.date?.getTime() ?? 0)
      );
    const byCreatedDesc = <T extends { createdAt?: Date }>(arr: T[]) =>
      [...arr].sort(
        (a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0)
      );

    return {
      stats: {
        candidaturesCount: myCandidatures.filter(
          (c) => (c.status ?? 'pending') !== 'rejected'
        ).length,
        candidaturesPendingCount: myCandidatures.filter(
          (c) => (c.status ?? 'pending') === 'pending'
        ).length,
        schoolsCount: mySchools.length,
        postsCount: myPosts.filter((p) => (p.status ?? 'pending') !== 'rejected')
          .length,
        activitiesValidatedCount: myActivities.filter(
          (a) => a.status === 'validated'
        ).length,
        paymentsCount: myPayments.length,
      },
      pendingCandidatures: byDateDesc(
        myCandidatures.filter((c) => (c.status ?? 'pending') === 'pending')
      ).slice(0, 5),
      activitiesToValidate: byDateDesc(
        myActivities.filter((a) => (a.status ?? 'pending') === 'pending')
      ).slice(0, 5),
      paymentsToPay: byCreatedDesc(
        myPayments.filter((p) => (p.status ?? 'pending') !== 'paid')
      ).slice(0, 5),
    };
  }, [
    isScopedRole,
    scopedCandidatures,
    candidatureSchoolById,
    posts,
    scopedActivities,
    activitySchoolById,
    scopedPayments,
    schools,
    scopedSchoolIds,
    inScope,
  ]);

  // Données effectivement affichées selon le mode.
  const displayStats = isScopedRole ? scoped?.stats : stats;
  const displayPendingCandidatures:
    | Array<{ id: string; userId?: string; postId?: string; date?: Date }>
    | undefined = isScopedRole ? scoped?.pendingCandidatures : pendingCandidatures;
  const displayActivitiesToValidate:
    | Array<{ id: string; candidatureId?: string; date?: Date }>
    | undefined = isScopedRole
    ? scoped?.activitiesToValidate
    : activitiesToValidate;
  const displayPaymentsToPay:
    | Array<{ id: string; amount?: number; status?: string; createdAt?: Date }>
    | undefined = isScopedRole ? scoped?.paymentsToPay : paymentsToPay;

  const candidatureLabelById = React.useMemo(() => {
    const map: Record<string, string> = {};
    displayPendingCandidatures?.forEach((c) => {
      const name = c.userId ? userNameById[c.userId] ?? c.userId : '';
      const postTitle = c.postId ? postTitleById[c.postId] ?? c.postId : '';
      const labelParts = [name, postTitle].filter(Boolean);
      if (labelParts.length) {
        map[c.id] = labelParts.join(' · ');
      }
    });
    return map;
  }, [displayPendingCandidatures, userNameById, postTitleById]);

  return (
    <Grid container spacing={3}>
      <Grid size={12}>
        <Typography variant="h5" gutterBottom>
          {isScopedRole ? 'Mon tableau de bord' : 'Dashboard'}
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Candidatures (hors rejetées)
          </Typography>
          <Typography variant="h4">
            {displayStats?.candidaturesCount ?? '—'}
          </Typography>
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Candidatures en attente
          </Typography>
          <Typography variant="h4">
            {displayStats?.candidaturesPendingCount ?? '—'}
          </Typography>
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Écoles actives
          </Typography>
          <Typography variant="h4">
            {displayStats?.schoolsCount ?? '—'}
          </Typography>
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Postes (hors rejetés)
          </Typography>
          <Typography variant="h4">
            {displayStats?.postsCount ?? '—'}
          </Typography>
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Activités validées
          </Typography>
          <Typography variant="h4">
            {displayStats?.activitiesValidatedCount ?? '—'}
          </Typography>
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Paiements
          </Typography>
          <Typography variant="h4">
            {displayStats?.paymentsCount ?? '—'}
          </Typography>
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Paper sx={{ p: 2, height: '100%' }}>
          <Typography variant="subtitle1" gutterBottom>
            Candidatures en attente
          </Typography>
          <List dense>
            {displayPendingCandidatures?.length ? (
              displayPendingCandidatures.map((c) => (
                <React.Fragment key={c.id}>
                  <ListItem>
                    <ListItemText
                      primary={
                        c.userId
                          ? userNameById[c.userId] ?? 'Candidature'
                          : 'Candidature'
                      }
                      secondary={`Poste: ${
                        c.postId ? postTitleById[c.postId] ?? c.postId : '—'
                      } · Date: ${formatDate(c.date)}`}
                    />
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                Aucune candidature en attente.
              </Typography>
            )}
          </List>
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Paper sx={{ p: 2, height: '100%' }}>
          <Typography variant="subtitle1" gutterBottom>
            Activités à valider
          </Typography>
          <List dense>
            {displayActivitiesToValidate?.length ? (
              displayActivitiesToValidate.map((a) => (
                <React.Fragment key={a.id}>
                  <ListItem>
                    <ListItemText
                      primary="Activité à valider"
                      secondary={`Candidature: ${
                        a.candidatureId
                          ? candidatureLabelById[a.candidatureId] ??
                            a.candidatureId
                          : '—'
                      } · Date: ${formatDate(a.date)}`}
                    />
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                Aucune activité en attente.
              </Typography>
            )}
          </List>
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Paper sx={{ p: 2, height: '100%' }}>
          <Typography variant="subtitle1" gutterBottom>
            Paiements à traiter
          </Typography>
          <List dense>
            {displayPaymentsToPay?.length ? (
              displayPaymentsToPay.map((p) => (
                <React.Fragment key={p.id}>
                  <ListItem>
                    <ListItemText
                      primary="Paiement à traiter"
                      secondary={`Montant: ${formatAmount(
                        p.amount
                      )} · Statut: ${paymentStatusLabel(
                        p.status
                      )} · Date: ${formatDate(p.createdAt)}`}
                    />
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                Aucun paiement en attente.
              </Typography>
            )}
          </List>
        </Paper>
      </Grid>
    </Grid>
  );
};
